import React, { useState } from "react";
import { ErrorIcon, MicIcon, SpinnerIcon } from "@/components/icons";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { clipMessage, type VoiceClipView } from "./clipList";

interface VoiceClipRowProps {
  view: VoiceClipView;
  onRetry: (clipId: string) => void;
  onDiscard: (clipId: string) => void;
  onRetryUpload: (localId: string) => void;
  onDiscardUpload: (localId: string) => void;
  /** Ask about Discard inside the row, not in a dialog: for rows in a
   * drawer, where a dialog would stack over it (DD-051). */
  confirmInline?: boolean;
}

/** Placeholder row for a voice clip that is uploading, transcribing or failed (DD-046). */
export function VoiceClipRow({
  view,
  onRetry,
  onDiscard,
  onRetryUpload,
  onDiscardUpload,
  confirmInline = false,
}: VoiceClipRowProps) {
  const [confirming, setConfirming] = useState(false);
  const busy = view.kind === "local" ? view.upload.status === "uploading" : view.clip.status !== "failed";
  // A rejected upload (HTTP 4xx on create) would fail the same way again: discard only.
  const retryable = view.kind === "local" ? view.upload.status === "upload_failed" : view.clip.retryable;

  const handleRetry = () => (view.kind === "local" ? onRetryUpload(view.upload.localId) : onRetry(view.clip.id));
  const handleDiscard = () => {
    setConfirming(false);
    if (view.kind === "local") onDiscardUpload(view.upload.localId);
    else onDiscard(view.clip.id);
  };

  let actions: React.ReactNode = null;
  if (!busy && confirmInline && confirming) {
    actions = (
      <div className="flex shrink-0 items-center gap-3">
        <span className="text-sm text-gray-700">Discard voice note?</span>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          className="text-sm font-medium text-gray-500 hover:text-gray-700"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleDiscard}
          className="text-sm font-medium text-red-600 hover:text-red-700"
        >
          Discard
        </button>
      </div>
    );
  } else if (!busy) {
    actions = (
      <div className="flex shrink-0 items-center gap-3">
        {retryable && (
          <button
            type="button"
            onClick={handleRetry}
            className="text-sm font-medium text-blue-600 hover:text-blue-700"
          >
            Retry
          </button>
        )}
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="text-sm font-medium text-gray-500 hover:text-gray-700"
        >
          Discard
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-3 py-2 last:border-b-0">
      <div className="flex min-w-0 items-center gap-2 text-sm">
        {busy ? (
          <SpinnerIcon className="w-4 h-4 shrink-0 text-blue-600" />
        ) : (
          <ErrorIcon className="w-4 h-4 shrink-0 text-amber-600" />
        )}
        <MicIcon className="w-4 h-4 shrink-0 text-gray-400" />
        <span className={busy ? "text-gray-700" : "text-amber-800"}>{clipMessage(view)}</span>
      </div>
      {actions}
      {!confirmInline && (
        <ConfirmDialog
          isOpen={confirming}
          onClose={() => setConfirming(false)}
          onConfirm={handleDiscard}
          title="Discard voice note"
          message="The recording will be deleted and can't be recovered."
          confirmText="Discard"
          variant="danger"
        />
      )}
    </div>
  );
}
